import React, { useState, useEffect } from 'react';
import { Banner } from '../components/common/Banner';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import PaletteIcon from '@mui/icons-material/Palette';
import DownloadIcon from '@mui/icons-material/Download';
import SpeedIcon from '@mui/icons-material/Speed';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Swal from 'sweetalert2';
import { useAuth } from '../context/authContext';
import { getAppMovilConfig, updateAppMovilConfig } from '../services/appMovilConfigService';

const FeatureCard = ({ icon, title, description }) => (
  <div className="group relative rounded-2xl bg-white ring-1 ring-black/5 shadow-sm p-5 flex flex-col items-center text-center gap-3 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:ring-[#0488AF]/30">
    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0488AF] to-[#09274A] flex items-center justify-center text-white shadow-md transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
      {icon}
    </div>
    <h4 className="text-base font-bold text-[#09274A]">{title}</h4>
    <p className="text-sm text-gray-500 leading-relaxed">{description}</p>
  </div>
);

const QrCard = ({ platform, icon, color, qrImage, storeUrl }) => (
  <div className="group relative rounded-2xl bg-white ring-1 ring-black/5 shadow-sm p-6 md:p-8 flex flex-col items-center gap-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg">
    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${color} flex items-center justify-center text-white shadow-md transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3`}>
      {icon}
    </div>
    <h3 className="text-xl font-bold text-[#09274A]">{platform}</h3>
    <div className="relative w-48 h-48 rounded-2xl bg-white flex items-center justify-center p-3 ring-1 ring-black/5 shadow-sm transition-all duration-300 group-hover:shadow-md group-hover:ring-[#0488AF]/30">
      <img src={qrImage} alt={`QR ${platform}`} className="w-full h-full object-contain" />
    </div>
    <a
      href={storeUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r ${color} shadow-md transition-all duration-300 hover:shadow-lg hover:scale-105 active:scale-95`}
    >
      <DownloadIcon sx={{ fontSize: 18 }} />
      Descargar en {platform === 'Android' ? 'Google Play' : 'App Store'}
    </a>
  </div>
);

export default function DownloadApp() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [appMovilEnabled, setAppMovilEnabled] = useState(null);
  const [appMovilLoading, setAppMovilLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await getAppMovilConfig();
        setAppMovilEnabled(cfg.enabled);
      } catch {
        setAppMovilEnabled(false);
      }
    })();
  }, []);

  const handleAppMovilToggle = async () => {
    try {
      setAppMovilLoading(true);
      const newValue = !appMovilEnabled;
      await updateAppMovilConfig(newValue);
      setAppMovilEnabled(newValue);
      window.dispatchEvent(new CustomEvent('app-movil-config-updated'));
      await Swal.fire({
        icon: 'success',
        title: newValue ? 'APP MÓVIL visible para socios' : 'APP MÓVIL oculta para socios',
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire('Error', error.message, 'error');
    } finally {
      setAppMovilLoading(false);
    }
  };

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      <Banner
        title="APP MÓVIL RDS"
        message="Accede a toda la información de Red de Socios CIDERE desde tu dispositivo móvil."
        className="mb-4"
      />

      {/* Toggle visibilidad APP MÓVIL para socios (solo admin) */}
      {isAdmin && appMovilEnabled !== null && (
        <div className="flex items-center justify-between rounded-xl bg-white ring-1 ring-black/5 shadow-sm px-5 py-3 mb-8">
          <div className="flex items-center gap-3">
            <SmartphoneIcon sx={{ fontSize: 22, color: '#0488AF' }} />
            <div>
              <div className="text-sm font-semibold text-gray-700">Mostrar APP MÓVIL a socios</div>
              <div className="text-xs text-gray-500">{appMovilEnabled ? 'Visible en el menú de socios' : 'Oculta para socios'}</div>
            </div>
          </div>
          <button
            onClick={handleAppMovilToggle}
            disabled={appMovilLoading}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              appMovilEnabled ? 'bg-[#0488AF]' : 'bg-gray-300'
            } ${appMovilLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                appMovilEnabled ? 'translate-x-6' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
      )}

      {/* Sección de propósito */}
      <div className="relative rounded-2xl bg-gradient-to-r from-[#E6F0FF] via-[#F3FAFF] to-[#E8F9FF] p-8 md:p-10 border border-blue-100/70 mb-10 overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#0488AF]/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#09274A]/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0488AF] to-[#09274A] flex items-center justify-center text-white shadow-md mx-auto mb-4">
            <SmartphoneIcon sx={{ fontSize: 32 }} />
          </div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-[#09274A] mb-4">
            ¿Por qué usar la app móvil?
          </h2>
          <p className="text-gray-600 leading-relaxed text-base md:text-lg">
            La aplicación móvil de Red de Socios CIDERE te permite llevar la gestión
            de tu empresa en tu bolsillo. Accede a convenios, vinculación comercial,
            documentación oficial, eventos y mucho más, desde cualquier lugar y en
            cualquier momento. Además, explora un mapa interactivo con socios
            geolocalizados para encontrar empresas y servicios cercanos a ti.
          </p>
        </div>
      </div>

      {/* Características */}
      <div className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#C42251]/30 to-transparent" />
          <h3 className="text-sm font-bold tracking-[0.2em] text-[#C42251] uppercase">
            Características principales
          </h3>
          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#C42251]/30 to-transparent" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <FeatureCard
            icon={<SpeedIcon sx={{ fontSize: 26 }} />}
            title="Acceso Rápido"
            description="Consulta información al instante desde tu móvil sin necesidad de un computador."
          />
          <FeatureCard
            icon={<NotificationsIcon sx={{ fontSize: 26 }} />}
            title="Notificaciones"
            description="Recibe alertas de nuevos convenios, eventos y documentación relevante."
          />
          <FeatureCard
            icon={<LocationOnIcon sx={{ fontSize: 26 }} />}
            title="Geolocalización"
            description="Encuentra empresas y socios cercanos gracias a la geolocalización integrada."
          />
          <FeatureCard
            icon={<PaletteIcon sx={{ fontSize: 26 }} />}
            title="Personalización"
            description="Configura la app a tu gusto con opciones de personalización y temas adaptables."
          />
        </div>
      </div>

      {/* QR Codes */}
      <div className="rounded-2xl bg-gradient-to-b from-[#F0F8FF] to-[#E8F4FD] border border-blue-100/60 p-8 md:p-10">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#0488AF]/30 to-transparent" />
          <h3 className="text-sm font-bold tracking-[0.2em] text-[#0488AF] uppercase">
            Descarga la app
          </h3>
          <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#0488AF]/30 to-transparent" />
        </div>
        <p className="text-sm text-gray-500 text-center mb-8 max-w-lg mx-auto">
          Escanea el código QR o haz clic en el botón para descargar la aplicación en tu tienda.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
          <QrCard
            platform="Android"
            icon={<AndroidIcon sx={{ fontSize: 30 }} />}
            color="from-[#43A047] to-[#2E7D32]"
            qrImage="/qr-google-play-store.png"
            storeUrl="https://play.google.com/store/apps/details?id=cl.rdscidere.rds&pcampaignid=web_share"
          />
          <QrCard
            platform="iPhone"
            icon={<AppleIcon sx={{ fontSize: 30 }} />}
            color="from-[#09274A] to-[#0488AF]"
            qrImage="/qr-app-store.png"
            storeUrl="https://apps.apple.com/us/app/rds-cidere/id6786018600"
          />
        </div>
      </div>

      <div className="text-center mt-8">
        <p className="text-sm text-gray-400">
          ¿Tienes problemas para descargar la app? Escríbenos a{' '}
          <span className="font-semibold text-gray-600">rds@cidere.cl</span>
        </p>
      </div>
    </div>
  );
}